import { useQuery } from "@tanstack/react-query";

import { API_ROUTES } from "../api/ApiRoutes";
import createPrivateAdminPanelInstance from "../api/PrivateAdminPanelInstance";
import type { StaffColors, StaffOption } from "../types/staff";

/** Backend returns list of strings. */
function formatLabel(s: string): string {
  if (!s) return s;
  return s
    .split(/[-_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

async function fetchStaffRolesDropdown(): Promise<StaffOption[]> {
  const token = JSON.parse(localStorage.getItem("token") || "null");
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);
  const response = await instance.get(API_ROUTES.STAFF_ROLES_DROPDOWN);
  const data = response?.data?.data ?? response?.data ?? response;
  const list = Array.isArray(data) ? data : (data?.roles ?? data?.items ?? []);
  
  // Backend returns dicts with role_id, role_name, and role_color
  return list.map((item: { role_id: string; role_name: string; role_color?: string } | string) => {
    if (typeof item === "string") {
      // Fallback for legacy string format
      return { value: item, label: formatLabel(item) };
    }
    return {
      value: item.role_id,
      label: item.role_name,
      color: item.role_color,
    };
  });
}

async function fetchStaffStatusDropdown(): Promise<StaffOption[]> {
  const token = JSON.parse(localStorage.getItem("token") || "null");
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);
  const response = await instance.get(API_ROUTES.STAFF_STATUS_DROPDOWN);
  const data = response?.data?.data ?? response?.data ?? response;
  const list = Array.isArray(data) ? data : (data?.statuses ?? data?.status ?? data?.items ?? []);
  
  // Backend returns dicts with status_name and status_value
  return list.map((item: { status_name: string; status_value: string } | string) => {
    if (typeof item === "string") {
      // Fallback for legacy string format
      return { value: item, label: formatLabel(item) };
    }
    return {
      value: item.status_value,
      label: item.status_name,
    };
  });
}

async function fetchStaffRainbow(): Promise<StaffOption[]> {
  const token = JSON.parse(localStorage.getItem("token") || "null");
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);
  const response = await instance.get(API_ROUTES.STAFF_RAINBOW);
  const data = response?.data?.data ?? response?.data ?? response;
  const list = Array.isArray(data) ? data : (data?.colors ?? data?.items ?? []);

  return list
    .map((item: { color?: string; hex?: string; value?: string; label?: string } | string) => {
      const raw = typeof item === "string" ? item : (item.color ?? item.hex ?? item.value ?? "");
      const color = String(raw || "").trim();
      if (!color) return null;
      return {
        value: color,
        label: typeof item === "string" ? color : (item.label ?? color),
        color,
      };
    })
    .filter((item: StaffColors | null): item is StaffOption => Boolean(item));
}

export function useGetStaffRolesDropdown() {
  return useQuery<StaffOption[], Error>({
    queryKey: ["GET_STAFF_ROLES_DROPDOWN"],
    queryFn: fetchStaffRolesDropdown,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
}

export function useGetStaffStatusDropdown() {
  return useQuery<StaffOption[], Error>({
    queryKey: ["GET_STAFF_STATUS_DROPDOWN"],
    queryFn: fetchStaffStatusDropdown,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
}

export function useGetStaffRainbow() {
  return useQuery<StaffOption[], Error>({
    queryKey: ["GET_STAFF_RAINBOW"],
    queryFn: fetchStaffRainbow,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
}
